import { useId, type ReactNode } from 'react';

import { safeId } from './labelledControl';

// ─────────────────────────────────────────────────────────────────────
// A MESSAGE THAT ANNOUNCES ITSELF, AND THAT A FIELD CAN POINT AT (#781 slice 10).
//
// The app says «feil passord» in a `<p className="text-rose">`. Sighted users
// see it appear; a screen-reader user is told nothing, because nothing in the
// markup says the paragraph is new or that it belongs to the field above it.
//
// Two things fix that, and both have to be in the same place:
//
//   • THE ROLE. `role="alert"` is an assertive live region — inserting it
//     interrupts and is announced. `role="status"` is polite and waits for a
//     pause. An error is the first case and a «lagret» confirmation is the
//     second, so the role follows `kind` rather than being a prop someone has
//     to remember.
//   • THE ID. A live region announces itself ONCE, when it appears. It does not
//     tell anyone which control it is about — that is `aria-describedby` on the
//     control, which needs an id to point at. So the message owns an id whether
//     or not the caller supplies one, and `Field` supplies one deliberately.
//
// NO ICON AND NO BOX. The existing error text in `LoginScreen` and
// The retired `/sa` account panel was `text-rose` at 11–12px, and this slice is semantics, not
// a redesign.
// ─────────────────────────────────────────────────────────────────────

export type StatusMessageKind = 'error' | 'success' | 'info';

export interface StatusMessageProps {
  /** The message. */
  children: ReactNode;
  /**
   * `error` announces assertively (`role="alert"`); `success` and `info`
   * announce politely (`role="status"`). Defaults to `error`, because that is
   * the case that must never be silent.
   */
  kind?: StatusMessageKind;
  /**
   * The id a control's `aria-describedby` points at. Generated when omitted, so
   * a standalone message is still addressable.
   */
  id?: string;
  /** Extra classes — spacing for the surface it sits on. */
  className?: string;
}

const TONE: Record<StatusMessageKind, string> = {
  error: 'text-rose',
  success: 'text-lime-deep',
  info: 'text-ink-3',
};

/**
 * An error / success / info message with the right live-region role and an id.
 *
 * ```tsx
 * <StatusMessage kind="error" id={errorId}>{t('auth.login.wrongPassword')}</StatusMessage>
 * ```
 *
 * The text itself is the CALLER's: a message primitive with its own copy would
 * either hard-code Bokmål or invent a generic sentence nobody wants. Codes come
 * from the API untranslated and the caller maps them through `useT()` — see
 * CLAUDE.md on why the server never sends a sentence.
 */
export function StatusMessage({ children, kind = 'error', id, className }: StatusMessageProps) {
  const auto = useId();
  return (
    <p
      id={id ?? safeId('status', auto)}
      role={kind === 'error' ? 'alert' : 'status'}
      className={`text-[11px] font-medium ${TONE[kind]}${className ? ` ${className}` : ''}`}
    >
      {children}
    </p>
  );
}
